import {PUNISHMENT_KEYS} from "@/api/punishmentApi";
import CloseIcon from "@/assets/colorable/close_x.svg?react";
import PostIcon from "@/assets/colorable/posts.svg?react";
import ReelIcon from "@/assets/colorable/reels.svg?react";
import StoryIcon from "@/assets/colorable/stories.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UgcIcon from "@/assets/colorable/ugc.svg?react";
import CompletionMarkers from "@/components/discipline/CompletionMarkers";
import type {CampaignApplicationPunishmentSerializer, PunishmentDetailSerializer} from "@/generated/Punishment";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {memo, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useMedia} from "react-use";

const FlaggedCampaign: FC<{
  data?: CampaignApplicationPunishmentSerializer;
  disciplineId?: number;
  onClose?: () => void;
}> = ({data, disciplineId, onClose}) => {
  const {t} = useTranslation("influencer_discipline");

  const {data: disciplineData} = useGenericQuery<PunishmentDetailSerializer>({
    queryKey: PUNISHMENT_KEYS.qPunishment(disciplineId!),
    enabled: !!disciplineId,
  });

  const campaignApplication = useMemo(
    () => data ?? disciplineData?.campaign_application,
    [data, disciplineData?.campaign_application],
  );

  const isMd = useMedia("(max-width: 768px)");

  if (!campaignApplication) {
    return null;
  }

  return (
    <div className="flex w-40 flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-neutral-black px-1 text-xs">{t("flagged_campaign")}</span>
        {onClose && (
          <button onClick={onClose}>
            <CloseIcon className="text-neutral-grey-light size-3" />
          </button>
        )}
      </div>
      <button
        className="border-primary-light-solid relative size-40 rounded-lg border-3"
        onClick={() => window.open(`/campaign/${campaignApplication.campaign.id}/`, "_blank")}>
        <img className="size-full rounded-md" src={campaignApplication.campaign.picture640!} />
        <div className="absolute top-0 size-full rounded-md bg-linear-(--black-bottom-gradient)">
          <div className="flex h-full items-end p-2">
            <span className="line-clamp-3 font-semibold text-white">{campaignApplication.campaign.title}</span>
          </div>
        </div>
      </button>
      <div className="flex max-md:flex-col">
        <CompletionMarkers
          completedCount={campaignApplication.posts_done}
          Icon={PostIcon}
          labelKey="common:posts"
          requiredCount={campaignApplication.campaign.number_of_feedposts}
          showDivider={
            !isMd && (!!campaignApplication.campaign.number_of_stories || !!campaignApplication.stories_done)
          }
        />
        <CompletionMarkers
          completedCount={campaignApplication.stories_done}
          Icon={StoryIcon}
          labelKey="common:stories"
          requiredCount={campaignApplication.campaign.number_of_stories}
          showDivider={!isMd && (!!campaignApplication.reels_done || !!campaignApplication.campaign.number_of_reels)}
        />
        <CompletionMarkers
          completedCount={campaignApplication.reels_done}
          Icon={ReelIcon}
          labelKey="common:reels"
          requiredCount={campaignApplication.campaign.number_of_reels}
        />
        <CompletionMarkers
          completedCount={campaignApplication.tiktok_number_of_medias_done}
          Icon={TiktokIcon}
          labelKey="common:tiktoks"
          requiredCount={campaignApplication.campaign.tiktok_number_of_medias}
        />
        <CompletionMarkers
          completedCount={campaignApplication.ugc_done}
          Icon={UgcIcon}
          labelKey="common:videos"
          requiredCount={campaignApplication.campaign.number_of_ugc}
        />
      </div>
    </div>
  );
};

export default memo(FlaggedCampaign);
